import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import '../../styles/auth/AuthPages.css';

/**
 * VerifyEmailPage - Verify email with token or show pending verification status
 */
export const VerifyEmailPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const email = searchParams.get('email');
  const pending = searchParams.get('pending') === 'true';

  const { verifyEmail, requestVerificationEmail, error, clearError } = useAuth();

  const [status, setStatus] = useState<'verifying' | 'success' | 'error' | 'pending'>('pending');
  const [resendSuccess, setResendSuccess] = useState(false);
  const [resendError, setResendError] = useState('');
  const [isResending, setIsResending] = useState(false);

  // Auto-verify if token is present
  useEffect(() => {
    let isMounted = true;

    if (token) {
      setStatus('verifying');
      verifyEmail({ token })
        .then(result => {
          if (isMounted) {
            if (result.success) {
              setStatus('success');
            } else {
              setStatus('error');
            }
          }
        })
        .catch(() => {
          if (isMounted) {
            setStatus('error');
          }
        });
    } else if (pending && email) {
      setStatus('pending');
    }

    return () => {
      isMounted = false;
    };
  }, [token, pending, email, verifyEmail]);

  const handleResendEmail = async () => {
    if (!email) return;

    clearError();
    setResendSuccess(false);
    setResendError('');
    setIsResending(true);

    try {
      const result = await requestVerificationEmail(email);

      if (result.success) {
        setResendSuccess(true);
      } else {
        setResendError(result.error || 'Erreur lors de l\'envoi');
      }
    } catch (err) {
      setResendError('Erreur lors de l\'envoi');
    } finally {
      setIsResending(false);
    }
  };

  // Verifying state
  if (status === 'verifying') {
    return (
      <div className="auth-page">
        <div className="auth-box">
          <header className="auth-header">
            <h1 className="auth-logo">Corpus</h1>
            <h2 className="auth-title">Vérification en cours...</h2>
          </header>

          <div style={{ display: 'flex', justifyContent: 'center', padding: '24px 0' }}>
            <span className="auth-spinner" style={{ width: 32, height: 32, borderWidth: 3 }} />
          </div>
        </div>
      </div>
    );
  }

  // Success state
  if (status === 'success') {
    return (
      <div className="auth-page">
        <div className="auth-box">
          <header className="auth-header">
            <h1 className="auth-logo">Corpus</h1>
            <h2 className="auth-title">Email vérifié !</h2>
          </header>

          <div className="auth-alert success" role="alert">
            <svg className="auth-alert-icon" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
            </svg>
            <div className="auth-alert-content">
              <p className="auth-alert-title">Compte activé</p>
              <p className="auth-alert-message">
                Votre adresse email a été vérifiée avec succès.
                Vous pouvez maintenant vous connecter à votre compte.
              </p>
            </div>
          </div>

          <div className="auth-links">
            <Link to="/login" className="auth-link">
              Se connecter
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Error state (from token verification)
  if (status === 'error') {
    return (
      <div className="auth-page">
        <div className="auth-box">
          <header className="auth-header">
            <h1 className="auth-logo">Corpus</h1>
            <h2 className="auth-title">Vérification échouée</h2>
          </header>

          <div className="auth-alert error" role="alert">
            <svg className="auth-alert-icon" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
            </svg>
            <div className="auth-alert-content">
              <p className="auth-alert-title">Lien invalide ou expiré</p>
              <p className="auth-alert-message">
                {error || 'Le lien de vérification est invalide ou a expiré. Veuillez demander un nouveau lien.'}
              </p>
            </div>
          </div>

          <div className="auth-links">
            <Link to="/login" className="auth-link">
              Retour à la connexion
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Pending verification state (no token, show instructions)
  return (
    <div className="auth-page">
      <div className="auth-box">
        <header className="auth-header">
          <h1 className="auth-logo">Corpus</h1>
          <h2 className="auth-title">Vérifiez votre email</h2>
        </header>

        <div className="auth-alert info" role="alert">
          <svg className="auth-alert-icon" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z" />
          </svg>
          <div className="auth-alert-content">
            <p className="auth-alert-title">Confirmez votre adresse email</p>
            <p className="auth-alert-message">
              Un email de vérification a été envoyé à{' '}
              {email ? <strong>{email}</strong> : 'votre adresse email'}.
              Cliquez sur le lien dans l'email pour activer votre compte.
            </p>
          </div>
        </div>

        {resendSuccess && (
          <div className="auth-alert success" role="alert" style={{ marginTop: 16 }}>
            <svg className="auth-alert-icon" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
            </svg>
            <div className="auth-alert-content">
              <p className="auth-alert-message">
                Un nouvel email de vérification a été envoyé.
              </p>
            </div>
          </div>
        )}

        {resendError && (
          <div className="auth-alert error" role="alert" style={{ marginTop: 16 }}>
            <svg className="auth-alert-icon" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
            </svg>
            <div className="auth-alert-content">
              <p className="auth-alert-message">{resendError}</p>
            </div>
          </div>
        )}

        {email && (
          <button
            type="button"
            className="auth-submit"
            onClick={handleResendEmail}
            disabled={isResending}
            style={{ marginTop: 24 }}
          >
            {isResending ? (
              <>
                <span className="auth-spinner" />
                Envoi...
              </>
            ) : (
              'Renvoyer l\'email'
            )}
          </button>
        )}

        <div className="auth-links">
          <Link to="/login" className="auth-link">
            Retour à la connexion
          </Link>
        </div>
      </div>
    </div>
  );
};
